/**
 * One place to get hold of the library's singleton elements: `<as-app>`,
 * `<as-configurator>` and `<as-header>`.
 *
 * Each element registers itself here from its connectedCallback and clears
 * itself on disconnect, so the sync accessors are a field read, not a DOM
 * query. Before the element connects they fall back to a tag query, which
 * still finds a parsed-but-not-yet-upgraded element (fine for attribute
 * reads, not for calling methods on it).
 *
 * Three forms, pick by what the caller can tolerate:
 *
 *   asApp()          // AsApp | null — devlogs when missing
 *   requireAsApp()   // AsApp — throws when missing
 *   await whenAsApp()// AsApp — resolves once connected
 *
 * `whenConfigurator()` goes one step further and resolves with the live
 * Paramate instance, which is what most add-on components actually want.
 *
 * All of these are also on `window.asGlobals` (see exports.ts) for apps that
 * load the bundle with a plain script tag and have no imports:
 *
 *   asGlobals.asApp().appData.foo
 *   const cfgr = await asGlobals.whenConfigurator();
 *
 * wcQry/wcQryAll are re-exported from here too, so "find an element" has one
 * home: the three singletons by name, everything else through wcQry.
 *
 * This module imports only types and devlog. Keep it that way: it is meant to
 * sit at the bottom of the dependency graph so any layer can use it.
 */

import { devlog } from './utils.ts';
import type { AsApp } from '../components/as-app/as-app.ts';
import type { AsConfigurator } from '../components/paramate/as-configurator.ts';
import type { TrHeader } from '../components/apps-header/as-header.ts';

export { wcQry, wcQryAll } from './wcUtil.ts';

type SingletonName = 'app' | 'configurator' | 'header';

type SingletonTypes = {
  app: AsApp;
  configurator: AsConfigurator;
  header: TrHeader;
};

const TAGS: Record<SingletonName, string> = {
  app: 'as-app',
  configurator: 'as-configurator',
  header: 'as-header',
};

type Slot<T extends HTMLElement> = {
  el: T | null;
  waiters: ((el: T) => void)[];
};

const slots: { [K in SingletonName]: Slot<SingletonTypes[K]> } = {
  app: { el: null, waiters: [] },
  configurator: { el: null, waiters: [] },
  header: { el: null, waiters: [] },
};

/** Claims the named slot for this element, called by the element itself from
 *  connectedCallback. Puts it where the sync accessors read it, and resolves
 *  every whenAsApp()/whenAsConfigurator()/whenAsHeader() promise still parked
 *  on that name. Nothing else drains those waiters. Internal. */
export function _registerSingleton<K extends SingletonName>(name: K, el: SingletonTypes[K]): void {
  const slot = slots[name] as Slot<SingletonTypes[K]>;
  slot.el = el;
  const waiters = slot.waiters;
  slot.waiters = [];
  for (const resolve of waiters) resolve(el);
}

/** Releases the named slot, called by the element itself from
 *  disconnectedCallback. Only clears the slot if this element is the one
 *  registered, so a stray disconnect of an older instance cannot wipe the
 *  live one. Internal. */
export function _unregisterSingleton<K extends SingletonName>(name: K, el: SingletonTypes[K]): void {
  const slot = slots[name] as Slot<SingletonTypes[K]>;
  if (slot.el === el) slot.el = null;
}

/** Test hook: forget everything. */
export function _resetSingletonsForTest(): void {
  for (const name of Object.keys(slots) as SingletonName[]) {
    slots[name].el = null;
    slots[name].waiters = [];
  }
}

function get<K extends SingletonName>(name: K): SingletonTypes[K] | null {
  const slot = slots[name] as Slot<SingletonTypes[K]>;
  if (slot.el) return slot.el;
  const found = typeof document !== 'undefined'
    ? document.querySelector(TAGS[name]) as SingletonTypes[K] | null
    : null;
  if (!found) devlog(`[singletons] <${TAGS[name]}> not found — not in the document yet, or not at all`);
  return found;
}

function require<K extends SingletonName>(name: K): SingletonTypes[K] {
  const el = get(name);
  if (!el) throw new Error(`[singletons] <${TAGS[name]}> is required here but was not found`);
  return el;
}

function when<K extends SingletonName>(name: K): Promise<SingletonTypes[K]> {
  const slot = slots[name] as Slot<SingletonTypes[K]>;
  if (slot.el) return Promise.resolve(slot.el);
  return new Promise<SingletonTypes[K]>(resolve => { slot.waiters.push(resolve); });
}

// --- sync, null when missing -------------------------------------------------

/** The `<as-app>` element, or null if it is not in the document. */
export function asApp(): AsApp | null { return get('app'); }
/** The `<as-configurator>` element, or null if it is not in the document. */
export function asConfigurator(): AsConfigurator | null { return get('configurator'); }
/** The `<as-header>` element, or null if it is not in the document. */
export function asHeader(): TrHeader | null { return get('header'); }

// --- sync, throws when missing -----------------------------------------------

/** The `<as-app>` element. Throws if it is not in the document. */
export function requireAsApp(): AsApp { return require('app'); }
/** The `<as-configurator>` element. Throws if it is not in the document. */
export function requireAsConfigurator(): AsConfigurator { return require('configurator'); }
/** The `<as-header>` element. Throws if it is not in the document. */
export function requireAsHeader(): TrHeader { return require('header'); }

// --- async, resolves once connected ------------------------------------------

/** Resolves with `<as-app>` as soon as it has connected. */
export function whenAsApp(): Promise<AsApp> { return when('app'); }
/** Resolves with `<as-configurator>` as soon as it has connected. Note this is
 *  the element; use whenConfigurator() for the Paramate instance inside it. */
export function whenAsConfigurator(): Promise<AsConfigurator> { return when('configurator'); }
/** Resolves with `<as-header>` as soon as it has connected. */
export function whenAsHeader(): Promise<TrHeader> { return when('header'); }

/** Resolves with the live Paramate configurator: waits for the element to
 *  connect, then for it to initialise. A session reinit keeps the same
 *  instance, so the result stays valid across a session restore. */
export async function whenConfigurator(): Promise<any> {
  const el = await whenAsConfigurator();
  return el.waitForConfigurator();
}

/**
 * Hands the live Paramate configurator to an element that needs one: at once
 * when it is already up, otherwise the moment init() binds it. `el` is the
 * element waiting, and a configurator that arrives after it has gone is
 * dropped.
 *
 * Use this rather than a request event answered by <as-configurator>. The
 * bundler decides which module evaluates first, and elements upgrade as their
 * module defines them, so an input can connect and ask before
 * <as-configurator> is defined at all. Nothing hears that request and the
 * input never gets a configurator to write to. This waits on the singleton
 * slot instead, which does not care who was defined first.
 */
export function withConfigurator(el: HTMLElement, fn: (configurator: any) => void): void {
  whenConfigurator().then(configurator => {
    if (el.isConnected) fn(configurator);
  });
}
